// Framework SOWs: the call-off milestones as a workbook.
//
// The rows are the framework roll-up the Milestones tab draws (see
// src/api/frameworkRollup.js), so the export reconciles with the screen: the
// same call-off milestones, placed against the same pot, with draft and
// cancelled call-offs and cancelled, split or credit-noted milestones left out.
// Two sheets: a Summary with each pot's value, called off, invoiced and left,
// and every call-off milestone with its dates, invoice and PM. Written by
// utils/xlsxWriter.js (pen test AD25996, AD17), styled as the Bid Tracker
// report is.
import { downloadXlsx, colName } from '../../utils/xlsxWriter.js';

const BORDER = { style: 'thin', color: { rgb: 'FFD1D5DB' } };
const border = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const headerStyle = {
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'FF0D1D32' } },
  alignment: { vertical: 'center', wrapText: true },
  border,
};
const bodyStyle = { font: { name: 'Calibri', sz: 11 }, alignment: { vertical: 'top', wrapText: true }, border };
const totalStyle = { ...bodyStyle, font: { name: 'Calibri', sz: 11, bold: true } };
const MONEY_FORMAT = '£#,##0.00';
const DATE_FORMAT = 'dd mmm yyyy';

export const SUMMARY_HEADERS = ['Pot', 'Amount', 'Called off', 'Invoiced', 'Left'];
export const CALL_OFF_HEADERS = [
  'Pot', 'Call-off SOW', 'Call-off title', 'Call-off status', 'Milestone', 'Description', 'Status',
  'Contract date', 'Forecast date', 'Amount', 'Invoice number', 'Invoice date', 'Paid date', 'PM',
];
const SUMMARY_MONEY = [1, 2, 3, 4];
const CALL_OFF_MONEY = [CALL_OFF_HEADERS.indexOf('Amount')];
const CALL_OFF_DATES = ['Contract date', 'Forecast date', 'Invoice date', 'Paid date'].map(h => CALL_OFF_HEADERS.indexOf(h));

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// A stored 'YYYY-MM-DD' as a Date at local midnight, so Excel shows that day.
function toDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : '';
}

/**
 * The two tables as plain values: { summary, callOffs }, each a list of rows
 * in header order. pots are the framework's own milestones, in screen order;
 * rollup is sow.framework_rollup.
 */
export function buildCallOffRows(pots, rollup) {
  const groups = pots
    .filter(p => rollup.lines[p.id])
    .map(p => ({ label: [p.milestone_code, p.description].filter(Boolean).join(' '), pot: round2(p.invoice_amount_excl), line: rollup.lines[p.id] }));
  if (rollup.unmatched.items.length) groups.push({ label: 'Unmatched', pot: null, line: rollup.unmatched });

  const summary = groups.map(g => [g.label, g.pot ?? '', g.line.called_off, g.line.invoiced, g.pot == null ? '' : round2(g.pot - g.line.called_off)]);
  const sum = (c) => round2(summary.reduce((t, r) => t + (Number(r[c]) || 0), 0));
  // Left in total is the footer's figure: everything called off, unmatched
  // included, against the framework's total.
  summary.push(['Total', sum(1), sum(2), sum(3), round2(sum(1) - sum(2))]);

  const callOffs = groups.flatMap(g => g.line.items.map(i => [
    g.label, i.sow_reference, i.sow_detail || '', i.sow_status || '', i.milestone_code, i.description || '', i.status,
    toDate(i.contract_date), toDate(i.forecast_date), i.amount, i.invoice_number || '',
    toDate(i.formal_invoice_date), toDate(i.paid_date), i.pm_name || '',
  ]));
  return { summary, callOffs };
}

// A column is as wide as its longest cell, within reason.
function widths(headers, rows) {
  return headers.map((h, c) => {
    let w = String(h).length;
    for (const r of rows) w = Math.max(w, r[c] instanceof Date ? 11 : String(r[c] ?? '').length);
    return { wch: Math.min(50, Math.max(10, w + 2)) };
  });
}

function sheet(name, headers, rows, { money, dates = [], totalRow = false }) {
  const last = rows.length - 1;
  return {
    name,
    rows: [
      headers.map(v => ({ v, s: headerStyle })),
      ...rows.map((row, r) => headers.map((_, c) => {
        const v = row[c] ?? '';
        const z = typeof v === 'number' && money.includes(c) ? MONEY_FORMAT : v instanceof Date && dates.includes(c) ? DATE_FORMAT : undefined;
        return { v, s: totalRow && r === last ? totalStyle : bodyStyle, z };
      })),
    ],
    cols: widths(headers, rows),
    autofilter: !totalRow && rows.length ? `A1:${colName(headers.length - 1)}${rows.length + 1}` : undefined,
    freeze: { ySplit: 1 },
  };
}

/** Build the workbook as a list of sheets for xlsxWriter. Returned rather than written so a caller can inspect it. */
export function buildCallOffWorkbook(pots, rollup) {
  const { summary, callOffs } = buildCallOffRows(pots, rollup);
  return [
    sheet('Summary', SUMMARY_HEADERS, summary, { money: SUMMARY_MONEY, totalRow: true }),
    sheet('Call-off milestones', CALL_OFF_HEADERS, callOffs, { money: CALL_OFF_MONEY, dates: CALL_OFF_DATES }),
  ];
}

/** Build and download the export: `<reference>-call-off-milestones.xlsx`. */
export function downloadCallOffExport(sow, pots, rollup) {
  downloadXlsx(buildCallOffWorkbook(pots, rollup), `${sow.reference || 'framework'}-call-off-milestones.xlsx`);
}
